// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { isUndefined } from '@shared/utils';

/** Type for a nested structure. */
type NestedStructure<T> = Array<T & { subRows?: NestedStructure<T> }>;

/**
 * Recursively searches through a nested data structure to find a specific property
 * based on a ticker identifier and property path.
 *
 * @example // Basic usage const logo = findNestedSubRowsProp<string>('AAPL',
 * dataSource, 'url_logo');
 *
 * @example // Accessing root-level properties const investment =
 * findNestedSubRowsProp<number>('AMZN', dataSource, 'price');
 *
 * @template T - The expected type of the property value to return
 * @param {string} identifier - The identifier to search a coincidence, like the
 *   ticker
 * @param {NestedStructure<Record<string, any>>} dataSource - The nested data
 *   structure to search through
 * @param {string} propertyToFind - Property to find in the dataSource directly or
 *   in nested subRows
 * @returns {T | undefined} The found property value or undefined if not found
 * @note Performs depth-first search through nested structures
 * @note Returns the first matching occurrence found in the traversal
 */
export const findNestedSubRowsProp = <T = unknown>(
  identifier: string,
  dataSource: NestedStructure<Record<string, any>>,
  propertyToFind: string,
): T | undefined => {
  const searchSubRows = (
    items: NestedStructure<Record<string, any>>,
  ): T | undefined => {
    for (const item of items) {
      if (item.ticker === identifier) {
        // Search direct in the item
        if (item[propertyToFind] !== undefined) {
          return item[propertyToFind] as T;
        }
        // Search in the item details
        if (item.details?.[propertyToFind] !== undefined) {
          return item.details[propertyToFind] as T;
        }
      }
      // Recursive search if subRows exist
      if (item.subRows?.length) {
        const found = searchSubRows(item.subRows);
        if (!isUndefined(found)) return found;
      }
    }
    return undefined;
  };

  return searchSubRows(Array.isArray(dataSource) ? dataSource : []);
};
